import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Cookie, ShieldCheck, X } from "lucide-react";

const CLE_CONSENTEMENT = "ppf-consentement-cookies";
const CLE_REDUIT = "ppf-cookies-reduit";

/* Consentement exprimé automatiquement : la validation de n'importe quel formulaire public
   (pré-enregistrement, souscription en ligne, contact) — qui exige d'accepter les Conditions
   Générales Contractuelles et la politique de confidentialité — vaut acceptation des cookies.
   Bandeau et icône disparaissent alors immédiatement (événement capté par le bandeau). */
export function accepterCookiesAutomatiquement() {
  window.localStorage.setItem(CLE_CONSENTEMENT, "accepte");
  window.localStorage.removeItem(CLE_REDUIT);
  window.dispatchEvent(new Event("ppf-consentement-cookies"));
}

/* Bandeau de consentement aux cookies — affiché à la PREMIÈRE connexion sur les pages
   visiteurs (jamais dans l'espace interne) : acceptation ou refus, lien vers la politique
   de confidentialité. Le choix est mémorisé localement et ne redemande plus jamais la main.
   La croix ne clôt jamais la question : tant qu'aucun choix n'est validé, le bandeau se
   replie en icône Cookies discrète en bas à gauche (toutes les pages publiques) — elle
   disparaît dès qu'un choix « accepter » ou « refuser » est exprimé. */
export default function BandeauCookies() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [reduit, setReduit] = useState(false);

  useEffect(() => {
    if (pathname.startsWith("/interne")) {
      setVisible(false);
      setReduit(false);
      return;
    }
    if (window.localStorage.getItem(CLE_CONSENTEMENT)) {
      setVisible(false);
      setReduit(false);
      return;
    }
    // Aucun choix du visiteur : bandeau complet, ou simple icône s'il l'avait replié
    if (window.localStorage.getItem(CLE_REDUIT) === "1") {
      setVisible(false);
      setReduit(true);
    } else {
      setVisible(true);
      setReduit(false);
    }
  }, [pathname]);

  // Mise à jour immédiate quand un consentement est enregistré (ex. : validation d'un formulaire public)
  useEffect(() => {
    const maj = () => {
      if (window.localStorage.getItem(CLE_CONSENTEMENT)) {
        setVisible(false);
        setReduit(false);
      }
    };
    window.addEventListener("ppf-consentement-cookies", maj);
    return () => window.removeEventListener("ppf-consentement-cookies", maj);
  }, []);

  const repondre = (choix) => {
    window.localStorage.setItem(CLE_CONSENTEMENT, choix);
    window.localStorage.removeItem(CLE_REDUIT);
    setVisible(false);
    setReduit(false);
  };

  // Croix : repli en icône — AUCUN choix enregistré (le visiteur décidera depuis l'icône)
  const replier = () => {
    window.localStorage.setItem(CLE_REDUIT, "1");
    setVisible(false);
    setReduit(true);
  };

  const reouvrir = () => {
    window.localStorage.removeItem(CLE_REDUIT);
    setReduit(false);
    setVisible(true);
  };

  if (!visible && !reduit) return null;

  if (reduit) {
    return (
      <motion.button
        type="button"
        onClick={reouvrir}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        title="Préférences cookies — accepter ou refuser"
        aria-label="Préférences cookies : ouvrir le bandeau pour accepter ou refuser"
        data-testid="cookies-icone-reduite"
        className="fixed bottom-[calc(0.75rem+env(safe-area-inset-bottom))] left-4 z-[70] flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.22)] transition-transform duration-200 hover:scale-105 active:scale-95"
      >
        <Cookie className="h-5 w-5 text-[#2b46c4]" />
        <span aria-hidden="true" className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-[#e0312e]" />
      </motion.button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, x: "-50%" }}
      animate={{ opacity: 1, y: 0, x: "-50%" }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="fixed bottom-[calc(0.75rem+env(safe-area-inset-bottom))] left-1/2 z-[70] w-[calc(100%-1.5rem)] max-w-3xl rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_24px_60px_-12px_rgba(15,23,42,0.35)] sm:p-6"
      data-testid="cookies-bandeau"
      role="dialog"
      aria-live="polite"
      aria-label="Consentement cookies"
    >
      <div className="flex items-start gap-4">
        <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2b46c4]/10 sm:flex">
          <Cookie className="h-5 w-5 text-[#2b46c4]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-bold text-[#101a33] sm:text-[14px]">
            Les cookies, en toute transparence
            <ShieldCheck className="ml-2 inline h-4 w-4 shrink-0 text-[#059669]" />
          </p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-slate-600 sm:text-[13px]">
            Nous utilisons des cookies nécessaires au bon fonctionnement du site. Vous pouvez les accepter ou les
            refuser —{" "}
            <Link
              to="/politique-de-confidentialite"
              className="font-semibold text-[#2b46c4] underline decoration-[#2b46c4]/50 underline-offset-2 transition-colors hover:decoration-[#2b46c4]"
              data-testid="cookies-lien-politique"
            >
              consultez notre politique de confidentialité
            </Link>
            .
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2.5 sm:mt-4 sm:gap-3">
            <button
              type="button"
              onClick={() => repondre("accepte")}
              data-testid="cookies-accepter-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#2b46c4] px-5 py-2.5 text-[13px] font-bold text-white transition-all duration-200 hover:bg-[#1e3299] active:scale-95"
            >
              Tout accepter
            </button>
            <button
              type="button"
              onClick={() => repondre("refuse")}
              data-testid="cookies-refuser-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-300 px-5 py-2.5 text-[13px] font-bold text-slate-600 transition-all duration-200 hover:border-slate-400 hover:bg-slate-50 active:scale-95"
            >
              Tout refuser
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={replier}
          title="Réduire — un choix reste à faire"
          data-testid="cookies-fermer-btn"
          className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}